import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, CheckCircle, AlertCircle, Building2, User, MapPin, CreditCard } from 'lucide-react';
import { cadastroPublicoService } from '../../services/aprovacoes';
import toast from 'react-hot-toast';
import { CpfInput, PhoneInput, CepInput } from '../../components/MaskedInputs';
import { mensagemDoErro } from '../../utils/erros';
import { cpfValido } from '../../utils/documentos';
import CampoCidadeUf from '../../components/CampoCidadeUf';

const CadastroMotorista: React.FC = () => {
  const [step, setStep] = useState<'codigo' | 'form' | 'sucesso'>('codigo');
  const [codigo, setCodigo] = useState('');
  const [empresaNome, setEmpresaNome] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    codigo_cadastro: '',
    nome: '', cpf: '', rg: '', data_nascimento: '',
    estado_civil: 'solteiro', sexo: 'M',
    celular: '', telefone: '', email: '',
    cep: '', endereco: '', numero: '', complemento: '',
    bairro: '', cidade: '', estado: '',
    cnh: '', categoria_cnh: 'B', vencimento_cnh: '', primeira_habilitacao: '',
  });

  const handleValidarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await cadastroPublicoService.validarCodigo(codigo);
      if (res.success) {
        setEmpresaNome(res.data.empresa_nome);
        setForm({ ...form, codigo_cadastro: codigo.toUpperCase() });
        setStep('form');
      }
    } catch (err: any) {
      toast.error(mensagemDoErro(err, 'Código inválido ou empresa inativa'));
    } finally {
      setLoading(false);
    }
  };

  // Campos que são persistidos como dígitos puros. Sem esta normalização, colar
  // "123.456.789-01" num input com maxLength={11} gravava "123.456.789" — o índice
  // unique não reconhece isso como duplicata do mesmo CPF em formato limpo, e o
  // mesmo cidadão acaba cadastrado duas vezes na empresa.
  const somenteDigitos = ['cpf', 'cnh', 'telefone', 'celular', 'cep'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const valorFinal = somenteDigitos.includes(name) ? value.replace(/\D/g, '') : value;
    setForm({ ...form, [name]: valorFinal });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cpfValido(form.cpf)) {
      toast.error('CPF inválido. Confira os números.');
      return;
    }

    setLoading(true);
    try {
      const res = await cadastroPublicoService.cadastrar(form);
      if (res.success) {
        setStep('sucesso');
      }
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) {
        toast.error(Object.values(errors).flat()[0] as string);
      } else {
        toast.error(err?.response?.data?.message || 'Erro ao cadastrar');
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === 'sucesso') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Cadastro enviado!</h2>
            <p className="text-gray-600 mb-2">Seu cadastro como motorista na empresa <strong>{empresaNome}</strong> foi enviado com sucesso.</p>
            <p className="text-gray-500 text-sm mb-6">A empresa vai analisar seus dados e você receberá um e-mail com o resultado.</p>
            <div className="bg-yellow-50 rounded-lg p-4 mb-6">
              <AlertCircle className="h-5 w-5 text-yellow-600 mx-auto mb-2" />
              <p className="text-sm text-yellow-700">Aguarde o contato da empresa. O prazo de análise pode variar.</p>
            </div>
            <Link to="/login" className="text-blue-600 hover:text-blue-700 text-sm font-medium">Voltar para o login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'codigo') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center">
              <Car className="h-9 w-9 text-white" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">Cadastro de motorista</h2>
            <p className="mt-2 text-sm text-gray-600">Informe o código fornecido pela empresa</p>
          </div>
          <form onSubmit={handleValidarCodigo} className="bg-white rounded-xl shadow-lg p-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código de cadastro</label>
              <input type="text" value={codigo} onChange={e => setCodigo(e.target.value.toUpperCase())}
                required maxLength={10} placeholder="Ex: ABC12DEF"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest font-mono uppercase" />
              <p className="text-xs text-gray-500 mt-2">Solicite este código ao administrador da empresa</p>
            </div>
            <button type="submit" disabled={loading || codigo.length < 3}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors">
              {loading ? 'Validando...' : 'Continuar'}
            </button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700">Voltar para o login</Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Cadastro de motorista</h2>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Building2 className="h-4 w-4 text-blue-600" />
            <span className="text-blue-600 font-medium">{empresaNome}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Dados pessoais</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                <input name="nome" required value={form.nome} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <CpfInput name="cpf" label="CPF" required value={form.cpf} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                {form.cpf.length === 11 && !cpfValido(form.cpf) && (
                  <p className="mt-1 text-xs text-red-600">CPF inválido. Confira os números.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RG *</label>
                <input name="rg" required value={form.rg} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento *</label>
                <input name="data_nascimento" type="date" required value={form.data_nascimento} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado Civil *</label>
                <select name="estado_civil" required value={form.estado_civil} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="solteiro">Solteiro(a)</option>
                  <option value="casado">Casado(a)</option>
                  <option value="divorciado">Divorciado(a)</option>
                  <option value="viuvo">Viuvo(a)</option>
                  <option value="separado">Separado(a)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sexo *</label>
                <select name="sexo" required value={form.sexo} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </div>
              <div>
                <PhoneInput name="celular" label="Celular" required value={form.celular} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <PhoneInput name="telefone" label="Telefone" value={form.telefone} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Para receber a notificação da aprovação"
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>
          </div>

          {/* Endereco */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Endereço</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CepInput name="cep" label="CEP" required value={form.cep} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                <input name="numero" required value={form.numero} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço *</label>
                <input name="endereco" required value={form.endereco} onChange={handleChange} placeholder="Rua, Avenida..."
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                <input name="complemento" value={form.complemento} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bairro *</label>
                <input name="bairro" required value={form.bairro} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <CampoCidadeUf
                uf={form.estado}
                cidade={form.cidade}
                required
                onChange={({ uf, cidade }) => setForm((f) => ({ ...f, estado: uf, cidade }))}
              />
            </div>
          </div>

          {/* CNH */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Habilitacao (CNH)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número da CNH *</label>
                <input name="cnh" required maxLength={11} value={form.cnh} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                <select name="categoria_cnh" required value={form.categoria_cnh} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  {['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento da CNH *</label>
                <input name="vencimento_cnh" type="date" required value={form.vencimento_cnh} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primeira Habilitacao *</label>
                <input name="primeira_habilitacao" type="date" required value={form.primeira_habilitacao} onChange={handleChange}
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>
          </div>

          {/* Botoes */}
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <button type="button" onClick={() => setStep('codigo')}
              className="px-6 py-3 border rounded-lg hover:bg-gray-50 font-medium">Voltar</button>
            <button type="submit" disabled={loading}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors">
              {loading ? 'Enviando...' : 'Enviar Cadastro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroMotorista;
